import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ArrowPathIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

/**
 * Lightbox Modal - Rendu via React Portal sur document.body
 * 
 * Garanties :
 * 1. Isolation totale de tout conteneur parent (évite les bugs de transform/stacking context).
 * 2. Recouvrement à 100% du viewport (100vw × 100vh) avec centrage absolu.
 * 3. Affichage exclusif de l'image sélectionnée (aucun encombrement secondaire).
 * 4. ZÉRO défilement (popup et page arrière-plan strictement bloqués).
 * 5. Adaptabilité responsive totale (desktop, tablette, mobile portrait/paysage).
 */
export default function Lightbox({
  src,
  alt = "Média agrandi",
  title,
  onClose,
  gallery = [],
  currentIndex = -1,
  onNavigate,
}) {
  const [currentMediaSrc, setCurrentMediaSrc] = useState(src);
  const [hasAttemptedFallback, setHasAttemptedFallback] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const displayTitle = title || alt;
  const isVideo = currentMediaSrc && currentMediaSrc.endsWith('.mp4');
  const hasGallery = gallery && gallery.length > 1;
  const currentNum = currentIndex >= 0 ? currentIndex + 1 : 1;
  const totalNum = gallery.length > 0 ? gallery.length : 1;

  // Déterminer une URL de secours automatique (ex: GitHub raw pour les communiqués)
  const getFallbackUrl = (url) => {
    if (!url || typeof url !== 'string') return null;
    if (url.includes('/communiques/')) {
      const parts = url.split('/communiques/');
      const filename = parts[parts.length - 1];
      if (filename && !url.startsWith('https://raw.githubusercontent.com')) {
        return `https://raw.githubusercontent.com/acemfes/acem_fes_communiques/main/images/${encodeURIComponent(filename)}`;
      }
    }
    return null;
  };

  // Réinitialisation de l'état lors d'un changement de photo
  useEffect(() => {
    setCurrentMediaSrc(src);
    setHasAttemptedFallback(false);
    setLoading(true);
    setError(false);
  }, [src, currentIndex]);

  // Verrouillage strict du défilement du body avec gestion de la barre de défilement
  useEffect(() => {
    const originalBodyOverflow = document.body.style.overflow;
    const originalBodyPaddingRight = document.body.style.paddingRight;
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

    document.body.style.overflow = 'hidden';
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    const preventDefaultScroll = (e) => {
      e.preventDefault();
    };

    window.addEventListener('touchmove', preventDefaultScroll, { passive: false });
    window.addEventListener('wheel', preventDefaultScroll, { passive: false });

    return () => {
      document.body.style.overflow = originalBodyOverflow;
      document.body.style.paddingRight = originalBodyPaddingRight;
      window.removeEventListener('touchmove', preventDefaultScroll);
      window.removeEventListener('wheel', preventDefaultScroll);
    };
  }, []);

  // Écoute des touches du clavier (Échap, Flèches Gauche/Droite)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (hasGallery && onNavigate) {
        if (e.key === 'ArrowRight') {
          onNavigate((currentIndex + 1) % gallery.length);
        } else if (e.key === 'ArrowLeft') {
          onNavigate((currentIndex - 1 + gallery.length) % gallery.length);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, gallery, currentIndex, onNavigate, hasGallery]);

  // Téléchargement du média
  const handleDownload = (e) => {
    e.stopPropagation();
    const downloadSrc = currentMediaSrc || src;
    if (!downloadSrc) return;
    const a = document.createElement('a');
    a.href = downloadSrc;
    a.download = `acem_fes_media_${Date.now()}`;
    a.target = '_blank';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleImageError = () => {
    const fallback = getFallbackUrl(currentMediaSrc);
    if (fallback && !hasAttemptedFallback) {
      setHasAttemptedFallback(true);
      setCurrentMediaSrc(fallback);
      setLoading(true);
    } else {
      setLoading(false);
      setError(true);
    }
  };

  if (!src && !currentMediaSrc) return null;

  // Structure JSX du Modal Lightbox
  const lightboxContent = (
    <div
      className="fixed inset-0 top-0 left-0 w-screen h-screen w-full h-full z-[999999] bg-black/95 backdrop-blur-md flex flex-col items-center justify-between overflow-hidden select-none touch-none animate-fade-in"
      onClick={onClose}
    >
      {/* 1. En-tête (Header Bar) */}
      <div
        className="w-full flex-shrink-0 px-4 sm:px-6 py-3 flex items-center justify-between bg-black/60 backdrop-blur-lg border-b border-white/10 z-50"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Titre & Compteur */}
        <div className="flex items-center gap-3 overflow-hidden">
          {hasGallery && (
            <span className="px-2.5 py-1 rounded-full bg-acem-primary/90 text-white text-xs font-bold font-mono tracking-wider shadow-sm flex-shrink-0">
              {currentNum} / {totalNum}
            </span>
          )}
          <h3 className="text-white text-xs sm:text-sm md:text-base font-semibold truncate max-w-[200px] sm:max-w-md md:max-w-xl">
            {displayTitle}
          </h3>
        </div>

        {/* Actions (Téléchargement & Fermeture) */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownload}
            className="p-2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-xl transition-all"
            title="Télécharger"
            aria-label="Télécharger"
          >
            <ArrowDownTrayIcon className="w-5 h-5" />
          </button>

          <button
            type="button"
            onClick={onClose}
            className="p-2 text-white/80 hover:text-rose-400 bg-white/10 hover:bg-white/20 rounded-xl transition-all"
            title="Fermer (Échap)"
            aria-label="Fermer"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
      </div>

      {/* 2. Zone d'affichage centrale - Image strictement contenue dans le Viewport */}
      <div
        className="relative flex-1 w-full h-full flex items-center justify-center p-3 sm:p-6 overflow-hidden"
        onClick={onClose}
      >
        {/* Flèche de navigation Gauche */}
        {hasGallery && onNavigate && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate((currentIndex - 1 + gallery.length) % gallery.length);
            }}
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-black/60 hover:bg-acem-primary text-white flex items-center justify-center transition-all z-50 border border-white/20 shadow-2xl backdrop-blur-md group"
            aria-label="Image précédente"
          >
            <ChevronLeftIcon className="w-6 h-6 sm:w-8 sm:h-8 group-hover:-translate-x-0.5 transition-transform" />
          </button>
        )}

        {/* Spinner de chargement */}
        {loading && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/80 pointer-events-none z-20">
            <ArrowPathIcon className="w-10 h-10 animate-spin text-acem-accent" />
            <span className="text-xs sm:text-sm font-medium tracking-wide">
              Chargement de l'image...
            </span>
          </div>
        )}

        {/* Message d'erreur avec bouton réessayer */}
        {error && (
          <div
            className="z-50 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 text-center max-w-xs sm:max-w-sm space-y-4 text-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <ExclamationTriangleIcon className="w-12 h-12 text-amber-400 mx-auto" />
            <div className="space-y-1">
              <h4 className="font-title font-bold text-base">Image temporairement indisponible</h4>
              <p className="text-xs text-white/75 leading-relaxed">
                Impossible d'afficher cette photo actuellement.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                setError(false);
                setLoading(true);
                setRetryCount((prev) => prev + 1);
              }}
              className="px-5 py-2 bg-acem-primary hover:bg-acem-primary-dark text-white rounded-full font-bold text-xs shadow-md transition-all inline-flex items-center gap-2"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Réessayer
            </button>
          </div>
        )}

        {/* Élément Média (Image ou Vidéo) : Strictement centré et contenu */}
        <div
          className="relative max-w-full max-h-full flex items-center justify-center overflow-hidden"
          onClick={(e) => e.stopPropagation()}
        >
          {isVideo ? (
            <video
              src={currentMediaSrc}
              controls
              autoPlay
              onLoadedData={() => setLoading(false)}
              onError={handleImageError}
              className="max-w-[90vw] max-h-[82vh] sm:max-w-[92vw] sm:max-h-[85vh] object-contain rounded-xl sm:rounded-2xl shadow-2xl"
            />
          ) : (
            <img
              key={`${currentMediaSrc}-${retryCount}`}
              src={retryCount > 0 ? `${currentMediaSrc}?retry=${retryCount}` : currentMediaSrc}
              alt={alt}
              onLoad={() => {
                setLoading(false);
                setError(false);
              }}
              onError={handleImageError}
              className={`max-w-[90vw] max-h-[82vh] sm:max-w-[92vw] sm:max-h-[85vh] object-contain rounded-xl sm:rounded-2xl shadow-2xl transition-opacity duration-300 ${
                loading ? 'opacity-0' : 'opacity-100'
              }`}
            />
          )}
        </div>

        {/* Flèche de navigation Droite */}
        {hasGallery && onNavigate && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onNavigate((currentIndex + 1) % gallery.length);
            }}
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 sm:w-14 sm:h-14 rounded-full bg-black/60 hover:bg-acem-primary text-white flex items-center justify-center transition-all z-50 border border-white/20 shadow-2xl backdrop-blur-md group"
            aria-label="Image suivante"
          >
            <ChevronRightIcon className="w-6 h-6 sm:w-8 sm:h-8 group-hover:translate-x-0.5 transition-transform" />
          </button>
        )}
      </div>

      {/* 3. Pied de page minimal (Footer Bar) */}
      <div className="w-full flex-shrink-0 py-2.5 px-4 bg-black/40 backdrop-blur-lg border-t border-white/10 text-center text-xs text-white/60">
        <span>Utilisez les flèches du clavier ou les boutons pour naviguer • Échap pour fermer</span>
      </div>
    </div>
  );

  // Téléportation vers document.body via React Portal
  return ReactDOM.createPortal(lightboxContent, document.body);
}
